import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import { Observable } from 'rxjs';

/**
 * ApiHeadersInterceptor
 * ─────────────────────
 * Automatically attaches the standard API headers (Authorization, Content-Type,
 * ngrok-skip-browser-warning) to every outgoing HTTP request that targets the
 * backend API.
 *
 * Because this interceptor is registered once in AppModule, every service in the
 * application gets the correct headers without needing its own `getHeaders()`
 * method or any knowledge of the auth token.
 *
 * Requests that already carry an Authorization header (e.g. third-party APIs
 * like Flutter Wave or Google) are passed through untouched.
 */
@Injectable()
export class ApiHeadersInterceptor implements HttpInterceptor {

  /** URL fragments that should NOT receive the auth token. */
  private readonly SKIP_AUTH_URLS: string[] = [
    'googleapis.com',
    'flutterwave.com',
    'vpic.nhtsa.dot.gov',
    'fcm.googleapis.com',
    '/auth/login',
    '/auth/mfa/',
    '/auth/refresh',
    'verifast-api.ecl-global.com',
    'verifast360.com'
  ];

  /** Read the token directly from localStorage — no DI needed. */
  private getToken(): string | null {
    try {
      const raw = localStorage.getItem('holl_auth_session');
      if (!raw) { return null; }
      const session = JSON.parse(raw);
      return session && session.token ? session.token : null;
    } catch (e) {
      return null;
    }
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Skip external / third-party URLs
    if (this.shouldSkip(req.url)) {
      return next.handle(req);
    }

    // Skip if the caller has already set its own Authorization that is NOT a Bearer
    // token (opt-out for third-party APIs that provide their own scheme).
    const existingAuth = req.headers.get('Authorization');
    if (existingAuth && existingAuth.indexOf('Bearer ') === -1) {
      return next.handle(req);
    }

    const token = this.getToken();

    // Build all headers in one clone so none are dropped across chained clones.
    const headersToSet: { [header: string]: string } = {
      'ngrok-skip-browser-warning': 'true'
    };

    // Only set Content-Type when there is no body-driven content type already set
    // (e.g. multipart/form-data sets its own boundary).
    if (!req.headers.has('Content-Type') && !(req.body instanceof FormData)) {
      headersToSet['Content-Type'] = 'application/json';
    }

    if (token) {
      headersToSet['Authorization'] = `Bearer ${token}`;
    }

    const cloned = req.clone({ setHeaders: headersToSet });

    return next.handle(cloned);
  }

  private shouldSkip(url: string): boolean {
    return this.SKIP_AUTH_URLS.some(fragment => url.includes(fragment));
  }
}
